import { useParams, Link } from 'react-router-dom';
import SeoHead from '@/components/base/SeoHead';
import Breadcrumb, { getBreadcrumbForArticle } from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const articleSchema = (article: typeof articles[0], canonicalUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.excerpt,
  author: {
    '@type': 'Organization',
    name: article.author,
  },
  datePublished: article.date,
  image: article.imageUrl,
  url: canonicalUrl,
  inLanguage: 'zh-CN',
  isPartOf: {
    '@type': 'WebSite',
    name: '91浏览器',
    url: 'https://mingcidianzi162.cn',
  },
});

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 pt-24 pb-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-950 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = getBreadcrumbForArticle(article.slug, article.category, article.title);
  const relatedArticles = articles.filter(
    (a) => article.relatedArticles.includes(a.slug)
  );
  const cat = categories.find((c) => c.slug === article.category);
  const canonicalUrl = `/article/${article.slug}`;

  return (
    <>
      <SeoHead
        title={`${article.title} - 91浏览器`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema(article, `https://mingcidianzi162.cn${canonicalUrl}`)}
        schemaType="Article"
      />

      <main className="min-h-screen bg-background-50 pt-24 pb-16">
        <article className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            <div>
              <div className="relative h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                {cat && (
                  <Link
                    to={`/category/${cat.slug}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                  >
                    {cat.name}
                  </Link>
                )}
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-foreground-400"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-foreground-400"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-foreground-400"></i>
                  阅读约{article.readTime}
                </span>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800
                  prose-headings:text-foreground-950 prose-headings:font-semibold
                  prose-h2:text-lg md:prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-base md:prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-li:text-foreground-700"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-links-line text-primary-500"></i>
                    相关文章
                  </h4>
                  <div className="space-y-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="flex gap-3 group"
                      >
                        <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={ra.imageUrl}
                            alt={ra.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {ra.title}
                          </h5>
                          <span className="text-xs text-foreground-500">{ra.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-folder-line text-primary-500"></i>
                    文章分类
                  </h4>
                  <div className="space-y-1">
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          c.slug === article.category
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                        }`}
                      >
                        <i className={`${c.icon} text-sm`}></i>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
    </>
  );
}